import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarPlus, CheckCircle2, Circle, CalendarCheck, MessageCircle } from "lucide-react";
import WhatsAppDialog from "@/components/crm/WhatsAppDialog";
import { api, errMsg, fmtShort } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/context/MetaContext";
import { Button } from "@/components/ui/button";
import { PageHeader, Empty, Skeletons } from "@/components/crm/bits";

const TABS = [["today", "Due today"], ["overdue", "Overdue"], ["upcoming", "Upcoming"], ["done", "Completed"]];
const TYPE = { follow_up: "Follow-up", call: "Call", meeting: "Meeting", email: "Email", quotation: "Quotation", other: "Other" };

export default function Tasks() {
  const { can } = useAuth();
  const { setTaskOpen, version, bump } = useMeta();
  const [tab, setTab] = useState("today");
  const [scope, setScope] = useState("mine");
  const [tasks, setTasks] = useState(null);
  const [wa, setWa] = useState(null);
  const nav = useNavigate();
  const load = () => api.get("/tasks", { params: { status: tab, scope } }).then((r) => setTasks(r.data));
  useEffect(() => { setTasks(null); load(); }, [tab, scope, version]); // eslint-disable-line
  const toggle = async (t) => {
    try { await api.patch(`/tasks/${t.task_id}`, { done: !t.done }); toast.success(t.done ? "Reopened" : "Completed — logged on the lead"); bump(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="fade-in" data-testid="tasks-page">
      <PageHeader title="Follow-ups & Tasks" subtitle="Calls, meetings and reminders across your leads.">
        {can("task:view_all") && (
          <div className="inline-flex rounded-lg bg-white border border-gray-200 p-0.5" data-testid="task-scope-toggle">
            {[["mine", "Mine"], ["all", "Team"]].map(([v, l]) => <button key={v} data-testid={`task-scope-${v}`} onClick={() => setScope(v)} className={`px-3 h-8 rounded-md text-sm ${scope === v ? "bg-[#ECF5F1] text-[#0B3C2D] font-medium" : "text-gray-500"}`}>{l}</button>)}
          </div>
        )}
        <Button className="bg-[#0B3C2D] hover:bg-[#072B20] h-9" onClick={() => setTaskOpen(true)} data-testid="tasks-add-button"><CalendarPlus className="h-4 w-4 mr-1.5" />Schedule follow-up</Button>
      </PageHeader>
      <div className="flex gap-1 border-b border-gray-200 mb-4 overflow-x-auto" data-testid="task-tabs">
        {TABS.map(([v, l]) => <button key={v} onClick={() => setTab(v)} data-testid={`task-tab-${v}`} className={`px-3 pb-2.5 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === v ? "border-[#0B3C2D] text-[#0B3C2D] font-medium" : "border-transparent text-gray-500 hover:text-gray-800"}`}>{l}</button>)}
      </div>
      <div className="card-surface">
        {!tasks ? <div className="p-4"><Skeletons /></div> : tasks.length === 0 ? <Empty icon={CalendarCheck} title="Nothing here" text={tab === "overdue" ? "No overdue follow-ups. Nicely done." : "No tasks in this view."} /> : (
          <div className="divide-y divide-gray-50">
            {tasks.map((t) => (
              <div key={t.task_id} className="flex items-center gap-3 px-4 py-3" data-testid={`task-row-${t.task_id}`}>
                <button onClick={() => toggle(t)} data-testid={`task-complete-${t.task_id}`}>{t.done ? <CheckCircle2 className="h-5 w-5 text-[#0B3C2D]" /> : <Circle className="h-5 w-5 text-gray-300 hover:text-[#0B3C2D]" />}</button>
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-medium ${t.done ? "line-through text-gray-400" : ""}`}>{t.title}</p>
                  <p className="text-xs text-gray-500">{TYPE[t.type]} · {t.company ? <button className="hover:underline text-[#0B3C2D]" onClick={() => nav(`/leads/${t.lead_id}`)}>{t.company}</button> : "No lead"}{scope === "all" ? ` · ${t.assignee_name}` : ""}</p>
                </div>
                <span className={`text-xs whitespace-nowrap ${t.state === "overdue" ? "text-red-600 font-medium" : t.state === "today" ? "text-amber-700 font-medium" : "text-gray-500"}`}>{t.state === "today" ? "Today" : fmtShort(t.due_date)}{t.due_time ? ` · ${t.due_time}` : ""}</span>
                {!t.done && t.lead_phone && (
                  <button title="WhatsApp" onClick={() => setWa({ lead_id: t.lead_id, company: t.company, contact_person: t.lead_contact, phone: t.lead_phone })} data-testid={`task-whatsapp-${t.task_id}`}
                    className="h-8 w-8 grid place-items-center rounded-lg hover:bg-[#ECF5F1]"><MessageCircle className="h-4 w-4 text-[#128C7E]" /></button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <WhatsAppDialog open={!!wa} onOpenChange={(o) => !o && setWa(null)} lead={wa} />
    </div>
  );
}
